// The southlands: the half of the map below the districts, in the same
// storybook hand but a different world — farm country in the west (a barn,
// an orchard, sheep), old ruins and a storyteller's cave in the hills, and
// the coast (a beach with huts, a lighthouse, a boardwalk with a Ferris
// wheel, a fortune teller and a café). Same parts as the districts:
// sprites, decals, fields, footprints, lights, anchors, walkways.
import { rng, inkPoly, inkRect, inkEllipse, line, roundRect, mix, withAlpha, inkedCircles, clipCircles, samplePath } from "./paint"
import { paintFlowerClump } from "./nature"
import { lampPost, lantern, shingles, star, cobbles, flagstones } from "./landmarks"

const HAND = '"Bradley Hand", "Segoe Print", "Chalkboard SE", "Comic Neue", cursive'

export function buildSouthlands(byKey, { coast, seed = 1 } = {}) {
    const out = { sprites: [], decals: [], fields: [], footprints: [], lights: [], anchors: {}, walkways: [] }
    const add = (part) => {
        if (!part) return
        ;["sprites", "decals", "fields", "footprints", "lights", "walkways"].forEach((k) => out[k].push(...(part[k] || [])))
        Object.assign(out.anchors, part.anchors || {})
    }
    const builders = { farmstand, orchard, paddock, picnic, ruins, cave, cabin, phonebox, lighthouse, cafe, boardwalk, fortune }
    Object.entries(builders).forEach(([key, build]) => { if (byKey[key]) add(build(byKey[key])) })
    if (coast) add(beach(coast, byKey, seed))
    return out
}

function sign(ctx, pal, x, y, text, color) {
    ctx.font = `700 11px ${HAND}`
    const w = ctx.measureText(text).width + 14
    roundRect(ctx, x - w / 2, y - 15, w, 17, 4)
    ctx.fillStyle = color || pal.cloth; ctx.fill()
    ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
    ctx.fillStyle = "#2E3431"; ctx.textAlign = "center"; ctx.textBaseline = "middle"
    ctx.fillText(text, x, y - 6.5)
    ctx.textBaseline = "alphabetic"
}

// ---- farm country: the barn, the silo and the farm stand ---------------------

function farmstand(l) {
    const baseY = l.y - 20
    const red = (pal) => (pal.night ? "#6E2E2A" : "#B8433A")
    return {
        sprites: [{
            // the barn
            x: l.x - 30, y: baseY, box: [-110, -170, 130, 8],
            paint(ctx, pal) {
                const ink = pal.ink, w = 150, h = 84
                inkPoly(ctx, [[w / 2, 0], [w / 2 + 30, -14], [w / 2 + 30, -h - 14], [w / 2, -h]], mix(red(pal), "#000000", 0.25), ink)
                // gambrel roof
                const roof = [[-w / 2 - 8, -h + 2], [-w / 2 + 8, -h - 42], [0, -h - 66], [w / 2 - 8, -h - 42], [w / 2 + 8, -h + 2]]
                inkPoly(ctx, [[0, -h - 66], [30, -h - 80], [w / 2 + 38, -h - 56], [w / 2 + 38, -h - 12], [w / 2 + 8, -h + 2], [w / 2 - 8, -h - 42]], mix(pal.roofDark, "#000000", 0.1), ink)
                inkPoly(ctx, [[-w / 2, 0], [w / 2, 0], [w / 2, -h], [w / 2 - 8, -h - 40], [0, -h - 62], [-w / 2 + 8, -h - 40], [-w / 2, -h]], red(pal), ink, { line: 1.8 })
                ctx.save(); ctx.beginPath(); ctx.rect(-w / 2, -h - 62, w, h + 62); ctx.clip()
                for (let x = -w / 2 + 10; x < w / 2; x += 10) line(ctx, [[x, -h - 62], [x, 0]], mix(red(pal), "#000000", 0.2), 1, 0.7)
                ctx.restore()
                inkPoly(ctx, roof, pal.roofDark, ink, { line: 1.8 })
                // white trim, big X doors and the hayloft
                ctx.strokeStyle = pal.cloth; ctx.lineWidth = 3
                ctx.strokeRect(-26, -56, 52, 56)
                ;[[-26, -56, 26, 0], [26, -56, -26, 0]].forEach(([a, b, c, d]) => { ctx.beginPath(); ctx.moveTo(a, b); ctx.lineTo(c, d); ctx.stroke() })
                ctx.beginPath(); ctx.moveTo(0, -56); ctx.lineTo(0, 0); ctx.stroke()
                inkRect(ctx, -14, -h - 30, 28, 22, "#3A2A22", ink, { line: 1.4 })
                ctx.strokeStyle = pal.cloth; ctx.lineWidth = 2; ctx.strokeRect(-14, -h - 30, 28, 22)
                ;[-52, 52].forEach((x) => { inkRect(ctx, x - 10, -60, 20, 18, pal.night ? pal.windowLit : pal.window, ink, { line: 1.3 }); ctx.strokeStyle = pal.cloth; ctx.lineWidth = 2; ctx.strokeRect(x - 10, -60, 20, 18) })
            }
        }, {
            // the silo
            x: l.x + 120, y: baseY - 4, box: [-30, -170, 30, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                inkPoly(ctx, [[-22, 0], [22, 0], [22, -130], [-22, -130]], pal.night ? "#6A6E6A" : "#C9C6BC", ink, { line: 1.6 })
                ctx.save(); ctx.beginPath(); ctx.rect(-22, -130, 44, 130); ctx.clip()
                ctx.fillStyle = pal.night ? "#555956" : "#AFAca2"; ctx.fillRect(6, -130, 20, 130)
                for (let y = -12; y > -130; y -= 16) line(ctx, [[-22, y], [22, y]], pal.stoneDark, 1, 0.8)
                ctx.restore()
                ctx.beginPath(); ctx.moveTo(-24, -128); ctx.quadraticCurveTo(0, -168, 24, -128); ctx.closePath()
                ctx.fillStyle = pal.metal; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.6; ctx.stroke()
                inkEllipse(ctx, 0, 0, 22, 6, null, ink, 1.2)
            }
        }, {
            // the farm stand out front
            x: l.x + 20, y: l.y + 44, box: [-60, -80, 60, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[-44, 44].forEach((x) => line(ctx, [[x, 0], [x, -58]], pal.woodDark, 3))
                inkRect(ctx, -48, -24, 96, 24, pal.wood, ink, { line: 1.5 })
                const r = rng(701)
                ;[["#D2544F", -34], ["#E8894A", -12], ["#F2C14E", 10], ["#8FB061", 32]].forEach(([c, x]) => {
                    inkRect(ctx, x - 9, -34, 20, 10, pal.woodLight, ink, { line: 0.9 })
                    for (let k = 0; k < 5; k++) { ctx.fillStyle = c; ctx.beginPath(); ctx.arc(x - 5 + r() * 12, -36 - r() * 3, 3.2, 0, Math.PI * 2); ctx.fill() }
                })
                inkPoly(ctx, [[-54, -56], [54, -56], [46, -70], [-46, -70]], pal.roof, ink, { line: 1.5 })
                sign(ctx, pal, 0, -41, "FARM STAND", "#FFF6D8")
            }
        }, hayBale(l.x - 150, l.y + 30), hayBale(l.x - 120, l.y + 56)],
        decals: [(ctx, pal) => {
            // a packed-earth yard with wheel ruts
            ctx.save(); ctx.fillStyle = withAlpha(pal.pathDark, 0.5)
            ctx.beginPath(); ctx.ellipse(l.x, l.y + 20, 170, 60, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore()
            line(ctx, [[l.x - 120, l.y + 40], [l.x + 100, l.y + 30]], withAlpha(pal.pathDark, 0.9), 3)
            line(ctx, [[l.x - 120, l.y + 52], [l.x + 100, l.y + 42]], withAlpha(pal.pathDark, 0.9), 3)
        }],
        fields: [(ctx, pal) => {
            // a pumpkin patch behind the barn
            const r = rng(702)
            ctx.save(); ctx.fillStyle = pal.night ? "#3A3A2C" : "#B69A6A"
            roundRect(ctx, l.x - 250, l.y - 150, 160, 110, 14); ctx.fill(); ctx.restore()
            for (let i = 0; i < 26; i++) {
                const x = l.x - 240 + r() * 140, y = l.y - 140 + r() * 90
                inkEllipse(ctx, x, y, 6 + r() * 3, 4.5 + r() * 2, pal.night ? "#8A5A2A" : "#E8894A", pal.ink, 1)
                line(ctx, [[x, y - 4], [x + 1, y - 7]], pal.leafDark, 1.4)
            }
        }],
        footprints: [{ x: l.x - 112, y: baseY - 46, w: 190, h: 48 }, { circle: true, x: l.x + 120, y: baseY - 8, r: 24 }, { x: l.x - 30, y: l.y + 30, w: 100, h: 16 }],
        lights: [{ x: l.x - 82, y: baseY - 50, r: 60 }, { x: l.x + 22, y: baseY - 50, r: 60 }, { x: l.x + 20, y: l.y + 10, r: 60 }]
    }
}

function hayBale(x, y) {
    return {
        x, y, box: [-22, -30, 22, 6],
        paint(ctx, pal) {
            inkPoly(ctx, [[-18, 0], [18, 0], [18, -20], [-18, -20]], pal.night ? "#6A5E3A" : "#E3C06A", pal.ink, { line: 1.3 })
            inkEllipse(ctx, 18, -10, 7, 10, pal.night ? "#7A6E48" : "#EED28A", pal.ink, 1.2)
            for (let i = 0; i < 4; i++) line(ctx, [[-16, -4 - i * 4], [14, -3 - i * 4]], pal.night ? "#4E4428" : "#C9A04A", 0.9, 0.8)
        }
    }
}

// ---- farm country: the orchard ------------------------------------------------
// Rows of apple trees on a grid, each a little different, with baskets and a
// ladder against one of them.

function orchard(l) {
    const sprites = [], footprints = []
    const r = rng(711)
    for (let row = 0; row < 5; row++) {
        for (let col = 0; col < 5; col++) {
            const x = l.x - 200 + col * 100 + (row % 2) * 50 + (r() - 0.5) * 10
            const y = l.y - 160 + row * 80 + (r() - 0.5) * 8
            if (Math.hypot(x - l.x, (y - l.y) * 1.3) > l.r + 40) continue
            const s = 20 + r() * 5, treeSeed = Math.floor(r() * 1e6)
            sprites.push({ x, y, box: [-s * 1.4, -s * 2.9, s * 1.4, 6], kind: "tree", sway: 0.8, paint(ctx, pal) { appleTree(ctx, pal, s, treeSeed) } })
            footprints.push({ circle: true, x, y: y - 2, r: 8 })
        }
    }
    sprites.push({
        x: l.x + 70, y: l.y + 110, box: [-40, -40, 40, 6],
        paint(ctx, pal) {
            // baskets of apples
            ;[-18, 16].forEach((dx, i) => {
                inkPoly(ctx, [[dx - 14, -2], [dx - 11, -20], [dx + 11, -20], [dx + 14, -2]], pal.wood, pal.ink, { line: 1.2 })
                for (let k = 0; k < 5; k++) { ctx.fillStyle = i ? "#8FB061" : "#D2544F"; ctx.beginPath(); ctx.arc(dx - 8 + k * 4, -22 - (k % 2) * 3, 3.2, 0, Math.PI * 2); ctx.fill() }
            })
        }
    })
    return {
        sprites,
        decals: [(ctx, pal) => {
            // mown rows between the trees, and windfalls
            ctx.save()
            ctx.strokeStyle = withAlpha(pal.groundLight, 0.9); ctx.lineWidth = 26; ctx.lineCap = "round"
            for (let row = 0; row < 5; row++) { const y = l.y - 150 + row * 80; ctx.beginPath(); ctx.moveTo(l.x - 230, y); ctx.lineTo(l.x + 230, y); ctx.stroke() }
            ctx.restore()
            const rr = rng(712)
            for (let i = 0; i < 30; i++) { ctx.fillStyle = "#C8453A"; ctx.globalAlpha = 0.85; ctx.beginPath(); ctx.arc(l.x - 220 + rr() * 440, l.y - 170 + rr() * 340, 2.4, 0, Math.PI * 2); ctx.fill() }
            ctx.globalAlpha = 1
        }],
        footprints,
        lights: []
    }
}

function appleTree(ctx, pal, s, seed) {
    const r = rng(seed)
    inkPoly(ctx, [[-s * 0.2, 0], [-s * 0.12, -s * 0.9], [s * 0.12, -s * 0.9], [s * 0.2, 0]], pal.trunk, pal.ink, { line: 1.4 })
    const lobes = []
    for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + r()
        lobes.push([Math.cos(a) * s * 0.5, -s * 1.5 + Math.sin(a) * s * 0.35, s * (0.45 + r() * 0.15)])
    }
    lobes.push([0, -s * 1.6, s * 0.6])
    inkedCircles(ctx, lobes, pal.leafDark, pal.ink, 1.8)
    ctx.save(); clipCircles(ctx, lobes)
    lobes.forEach(([x, y, rad]) => { ctx.fillStyle = pal.leaf; ctx.beginPath(); ctx.arc(x - rad * 0.15, y - rad * 0.2, rad * 0.82, 0, Math.PI * 2); ctx.fill() })
    lobes.forEach(([x, y, rad]) => { if (x < 0) { ctx.fillStyle = pal.leafLight; ctx.beginPath(); ctx.arc(x - rad * 0.3, y - rad * 0.4, rad * 0.4, 0, Math.PI * 2); ctx.fill() } })
    ctx.restore()
    // apples
    for (let i = 0; i < 9; i++) {
        const [x, y, rad] = lobes[Math.floor(r() * lobes.length)]
        const ax = x + (r() - 0.5) * rad * 1.2, ay = y + (r() - 0.5) * rad
        ctx.fillStyle = pal.night ? "#8A3A34" : "#D8453C"
        ctx.beginPath(); ctx.arc(ax, ay, 2.8, 0, Math.PI * 2); ctx.fill()
        ctx.fillStyle = "rgba(255,255,255,0.5)"; ctx.beginPath(); ctx.arc(ax - 0.9, ay - 0.9, 0.9, 0, Math.PI * 2); ctx.fill()
    }
}

// ---- farm country: the sheep paddock ------------------------------------------
// A post-and-rail fence around a grassy paddock with a gate on the north
// side; the sheep themselves wander in the motion layer.

function paddock(l) {
    const rx = 140, ry = 80, posts = 26
    const gate = (i) => i === 19 || i === 20 // the north side, facing the road
    const pts = Array.from({ length: posts }, (_, i) => {
        const a = (i / posts) * Math.PI * 2
        return [l.x + Math.cos(a) * rx, l.y + Math.sin(a) * ry]
    })
    const sprites = pts.map(([x, y], i) => {
        const [nx, ny] = pts[(i + 1) % posts]
        return {
            x, y, box: [Math.min(0, nx - x) - 6, -30, Math.max(0, nx - x) + 6, Math.max(0, ny - y) + 6],
            paint(ctx, pal) {
                inkRect(ctx, -2.5, -22, 5, 22, pal.woodDark, pal.ink, { line: 1 })
                if (gate(i)) return
                ;[-16, -8].forEach((dy) => { line(ctx, [[0, dy], [nx - x, ny - y + dy]], pal.ink, 3.6); line(ctx, [[0, dy], [nx - x, ny - y + dy]], pal.woodLight, 2) })
            }
        }
    })
    sprites.push({
        // the water trough
        x: l.x + 60, y: l.y + 30, box: [-26, -18, 26, 6],
        paint(ctx, pal) {
            inkPoly(ctx, [[-22, 0], [22, 0], [24, -12], [-24, -12]], pal.woodDark, pal.ink, { line: 1.3 })
            inkEllipse(ctx, 0, -12, 22, 3, pal.water, pal.ink, 0.9)
        }
    })
    return {
        sprites,
        decals: [(ctx, pal) => {
            ctx.save()
            ctx.beginPath(); ctx.ellipse(l.x, l.y, rx, ry, 0, 0, Math.PI * 2)
            ctx.fillStyle = withAlpha(pal.groundLight, 0.9); ctx.fill()
            ctx.clip()
            const r = rng(721)
            for (let i = 0; i < 140; i++) { ctx.strokeStyle = withAlpha(pal.tuft, 0.6); ctx.lineWidth = 1; const x = l.x + (r() - 0.5) * rx * 2, y = l.y + (r() - 0.5) * ry * 2; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 1, y - 5); ctx.stroke() }
            ctx.restore()
        }],
        footprints: pts.filter((_, i) => !gate(i) && !gate((i + posts - 1) % posts)).flatMap(([x, y], i, arr) => {
            // posts and the rails between them
            return [{ circle: true, x, y: y - 2, r: 10 }]
        }).concat(pts.flatMap(([x, y], i) => {
            if (gate(i)) return []
            const [nx, ny] = pts[(i + 1) % posts]
            return [{ circle: true, x: (x + nx) / 2, y: (y + ny) / 2 - 2, r: 10 }]
        })),
        lights: [],
        anchors: { paddock: { x: l.x, y: l.y, rx: rx - 30, ry: ry - 22 } }
    }
}

// ---- farm country: the picnic lawn ----------------------------------------------

function picnic(l) {
    const blankets = [[-70, -10, 0.2, "#D2544F"], [60, -20, -0.15, "#3E6FB0"], [-10, 50, 0.05, "#E8B43A"]]
    return {
        sprites: [{
            // bunting between two poles, with the Meetups banner
            x: l.x, y: l.y - 70, box: [-130, -110, 130, 6],
            paint(ctx, pal) {
                ;[-116, 116].forEach((x) => line(ctx, [[x, 0], [x, -92]], pal.woodDark, 3))
                ctx.strokeStyle = pal.ink; ctx.lineWidth = 1
                ctx.beginPath(); ctx.moveTo(-116, -90); ctx.quadraticCurveTo(0, -66, 116, -90); ctx.stroke()
                for (let i = 1; i < 14; i++) {
                    const t = i / 14, x = -116 + 232 * t, y = -90 + 2 * t * (1 - t) * 24
                    inkPoly(ctx, [[x - 6, y], [x + 6, y], [x, y + 11]], pal.flag[i % pal.flag.length], pal.ink, { line: 0.8 })
                }
                sign(ctx, pal, 0, -58, "MEETUPS", "#FFF6D8")
            }
        }, ...blankets.map(([dx, dy], i) => ({
            // a basket on each blanket
            x: l.x + dx + 12, y: l.y + dy + 6, box: [-14, -20, 14, 4],
            paint(ctx, pal) {
                inkPoly(ctx, [[-10, 0], [-8, -10], [8, -10], [10, 0]], pal.wood, pal.ink, { line: 1.1 })
                ctx.strokeStyle = pal.woodDark; ctx.lineWidth = 1.6
                ctx.beginPath(); ctx.arc(0, -10, 7, Math.PI, 0); ctx.stroke()
                if (i === 1) { inkEllipse(ctx, -14, -2, 4, 3, "#F2C14E", pal.ink, 0.8) }
            }
        }))],
        decals: [(ctx, pal) => {
            blankets.forEach(([dx, dy, a, color]) => {
                ctx.save(); ctx.translate(l.x + dx, l.y + dy); ctx.rotate(a)
                ctx.fillStyle = color; ctx.fillRect(-36, -22, 72, 44)
                ctx.fillStyle = "rgba(255,255,255,0.75)"
                for (let x = 0; x < 6; x++) for (let y = 0; y < 4; y++) if ((x + y) % 2) ctx.fillRect(-36 + x * 12, -22 + y * 11, 12, 11)
                ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.strokeRect(-36, -22, 72, 44)
                ctx.restore()
            })
            for (let i = 0; i < 5; i++) paintFlowerClump(ctx, pal, l.x - 130 + i * 65, l.y + 90 + (i % 2) * 10, 731 + i)
        }],
        footprints: [],
        lights: [{ x: l.x, y: l.y - 140, r: 90 }]
    }
}

// ---- the hills: the old ruins ----------------------------------------------------

function ruins(l) {
    const r = rng(741)
    const columns = [[-100, -30, 1], [-60, -40, 0.55], [40, -46, 1], [90, -28, 0.35], [-20, -52, 0.8]]
    return {
        sprites: [...columns.map(([dx, dy, height]) => ({
            x: l.x + dx, y: l.y + dy, box: [-20, -120, 20, 8],
            paint(ctx, pal) {
                const ink = pal.ink, h = 104 * height
                inkRect(ctx, -14, -6, 28, 6, pal.stone, ink, { line: 1.2 })
                inkPoly(ctx, [[-10, -6], [-9, -h], [9, -h], [10, -6]], pal.stoneLight, ink, { line: 1.4 })
                for (let x = -6; x <= 6; x += 4) line(ctx, [[x, -8], [x * 0.9, -h + 2]], pal.stone, 1, 0.8)
                // broken tops are jagged; whole ones wear a capital
                if (height > 0.9) inkRect(ctx, -14, -h - 8, 28, 8, pal.stone, ink, { line: 1.3 })
                else inkPoly(ctx, [[-9, -h], [-4, -h - 7], [1, -h - 2], [5, -h - 9], [9, -h]], pal.stoneLight, ink, { line: 1.2 })
                ctx.fillStyle = pal.moss; ctx.globalAlpha = 0.8
                ctx.beginPath(); ctx.ellipse(-4, -h * 0.4, 5, 9, 0.3, 0, Math.PI * 2); ctx.fill()
                ctx.globalAlpha = 1
            }
        })), {
            // a surviving arch, with ivy
            x: l.x + 10, y: l.y - 60, box: [-60, -140, 60, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[-44, 44].forEach((x) => inkRect(ctx, x - 10, -100, 20, 100, pal.stoneLight, ink, { line: 1.4 }))
                ctx.beginPath(); ctx.moveTo(-54, -96); ctx.quadraticCurveTo(0, -150, 54, -96); ctx.lineTo(34, -96); ctx.quadraticCurveTo(0, -128, -34, -96); ctx.closePath()
                ctx.fillStyle = pal.stone; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.5; ctx.stroke()
                for (let i = 0; i < 18; i++) {
                    ctx.fillStyle = i % 3 ? pal.leaf : pal.leafDark
                    ctx.beginPath(); ctx.arc(-50 + (i % 6) * 3 + (i > 8 ? 90 : 0), -20 - i * 5 % 80, 3.4, 0, Math.PI * 2); ctx.fill()
                }
            }
        }, {
            // a fallen column and the plaque
            x: l.x + 60, y: l.y + 40, box: [-60, -30, 60, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                inkPoly(ctx, [[-50, -2], [20, -8], [22, -22], [-48, -16]], pal.stoneLight, ink, { line: 1.3 })
                inkEllipse(ctx, 21, -15, 5, 7, pal.stone, ink, 1.1)
                line(ctx, [[36, 0], [36, -18]], pal.woodDark, 2.4)
                inkRect(ctx, 26, -34, 22, 16, pal.woodLight, ink, { line: 1 })
                ctx.fillStyle = "#2E3431"; ctx.font = `700 5px ${HAND}`; ctx.textAlign = "center"; ctx.fillText("ON THIS", 37, -27); ctx.fillText("DAY…", 37, -21)
            }
        }],
        decals: [(ctx, pal) => {
            // an old tiled floor, broken and grown over
            ctx.save()
            ctx.beginPath(); ctx.ellipse(l.x, l.y - 10, 150, 60, 0, 0, Math.PI * 2); ctx.clip()
            for (let y = l.y - 70; y < l.y + 50; y += 16) for (let x = l.x - 150; x < l.x + 150; x += 22) {
                if (r() < 0.25) continue
                inkRect(ctx, x + (r() - 0.5) * 2, y, 20, 14, r() < 0.5 ? pal.stone : pal.stoneLight, withAlpha(pal.ink, 0.35), { line: 0.7 })
            }
            ctx.restore()
            for (let i = 0; i < 14; i++) inkEllipse(ctx, l.x - 150 + r() * 300, l.y - 60 + r() * 120, 3 + r() * 5, 2 + r() * 3, pal.stoneDark, withAlpha(pal.ink, 0.5), 0.8)
        }],
        footprints: columns.map(([dx, dy]) => ({ circle: true, x: l.x + dx, y: l.y + dy - 3, r: 12 })).concat([{ circle: true, x: l.x - 34, y: l.y - 62, r: 10 }, { circle: true, x: l.x + 54, y: l.y - 62, r: 10 }, { x: l.x + 8, y: l.y + 22, w: 74, h: 20 }]),
        lights: []
    }
}

// ---- the hills: the storyteller's cave ----------------------------------------------

function cave(l) {
    const baseY = l.y - 20
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-170, -170, 170, 10],
            paint(ctx, pal) {
                const ink = pal.ink
                // a rocky hill, lit from the upper left, with grass on top
                const hill = [[-160, 0], [-150, -60], [-110, -110], [-60, -140], [0, -150], [60, -138], [110, -104], [150, -54], [162, 0]]
                inkPoly(ctx, hill, pal.stoneDark, ink, { line: 2 })
                ctx.save(); ctx.beginPath(); hill.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
                ctx.fillStyle = pal.stone; ctx.beginPath(); ctx.ellipse(-60, -90, 110, 70, -0.3, 0, Math.PI * 2); ctx.fill()
                ctx.fillStyle = pal.stoneLight; ctx.beginPath(); ctx.ellipse(-80, -110, 50, 26, -0.4, 0, Math.PI * 2); ctx.fill()
                ;[[-120, -40], [-40, -70], [70, -80], [120, -30], [20, -110]].forEach(([x, y]) => { line(ctx, [[x - 20, y], [x + 16, y + 6]], pal.stoneDark, 1.4, 0.8) })
                ctx.fillStyle = pal.leaf
                ctx.beginPath(); ctx.moveTo(-120, -106); ctx.quadraticCurveTo(0, -176, 120, -100); ctx.quadraticCurveTo(60, -126, 0, -134); ctx.quadraticCurveTo(-60, -130, -120, -106); ctx.fill()
                ctx.restore()
                // the cave mouth
                ctx.beginPath(); ctx.moveTo(-44, 0); ctx.quadraticCurveTo(-48, -76, 0, -84); ctx.quadraticCurveTo(48, -76, 44, 0); ctx.closePath()
                const g = ctx.createRadialGradient(0, -20, 4, 0, -30, 70)
                g.addColorStop(0, pal.night ? "#3A2A1A" : "#2A221C"); g.addColorStop(1, "#141010")
                ctx.fillStyle = g; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 2; ctx.stroke()
                // lanterns either side and a sign
                ;[-58, 58].forEach((x) => { line(ctx, [[x, 0], [x, -54]], pal.woodDark, 2.4); lantern(ctx, pal, x, -48) })
            }
        }, ...[[-70, 60], [70, 60], [0, 90]].map(([dx, dy]) => ({
            // log seats round the storytelling fire
            x: l.x + dx, y: l.y + dy, box: [-26, -18, 26, 8],
            paint(ctx, pal) {
                inkPoly(ctx, [[-20, -2], [20, -2], [20, -12], [-20, -12]], pal.trunk, pal.ink, { line: 1.3 })
                inkEllipse(ctx, 20, -7, 4, 5, pal.woodLight, pal.ink, 1)
            }
        }))],
        decals: [(ctx, pal) => {
            // a ring of stones for the fire (its flame lives in the motion layer)
            for (let i = 0; i < 9; i++) {
                const a = (i / 9) * Math.PI * 2
                inkEllipse(ctx, l.x + Math.cos(a) * 18, l.y + 52 + Math.sin(a) * 10, 5, 3.5, pal.stone, pal.ink, 1)
            }
            line(ctx, [[l.x - 10, l.y + 55], [l.x + 10, l.y + 49]], pal.trunkDark, 4)
            line(ctx, [[l.x - 10, l.y + 49], [l.x + 10, l.y + 55]], pal.trunk, 4)
        }],
        footprints: [{ x: l.x - 158, y: baseY - 40, w: 110, h: 42 }, { x: l.x + 48, y: baseY - 40, w: 112, h: 42 }, { circle: true, x: l.x, y: l.y + 52, r: 18 }],
        lights: [{ x: l.x - 58, y: baseY - 48, r: 60 }, { x: l.x + 58, y: baseY - 48, r: 60 }, { x: l.x, y: l.y + 40, r: 110, warm: false }],
        anchors: { caveFire: { x: l.x, y: l.y + 52 } }
    }
}

// ---- the hills: the writer's cabin -----------------------------------------------------

function cabin(l) {
    const baseY = l.y - 10
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-90, -150, 110, 8],
            paint(ctx, pal) {
                const ink = pal.ink, w = 120, h = 60
                // log walls: stacked rounded logs with cut ends
                inkPoly(ctx, [[w / 2, 0], [w / 2 + 26, -12], [w / 2 + 26, -h - 12], [w / 2, -h]], pal.woodDark, ink)
                for (let y = 0; y > -h; y -= 10) {
                    inkRect(ctx, -w / 2 - 4, y - 10, w + 8, 10, pal.wood, ink, { line: 1.1 })
                    inkEllipse(ctx, -w / 2 - 4, y - 5, 3.5, 5, pal.woodLight, ink, 0.9)
                }
                // stone chimney on the side
                inkPoly(ctx, [[w / 2 - 6, -h + 10], [w / 2 + 16, -h + 10], [w / 2 + 14, -h - 70], [w / 2 - 4, -h - 70]], pal.stone, ink, { line: 1.4 })
                for (let y = -h; y > -h - 70; y -= 9) line(ctx, [[w / 2 - 5, y], [w / 2 + 15, y]], pal.stoneDark, 0.9, 0.8)
                // roof
                inkPoly(ctx, [[-w / 2 - 14, -h + 4], [0, -h - 54], [w / 2 + 14, -h + 4]], pal.roofDark, ink, { line: 1.8 })
                shingles(ctx, pal, [[0, -h - 54], [0, -h - 54], [w / 2 + 14, -h + 4], [-w / 2 - 14, -h + 4]], pal.roofDark, 5)
                // door, window and a little porch with a rocking chair
                inkRect(ctx, -12, -42, 24, 42, pal.door, ink, { line: 1.4 })
                inkRect(ctx, 22, -44, 22, 20, pal.night ? pal.windowLit : pal.window, ink, { line: 1.3 })
                line(ctx, [[33, -44], [33, -24]], pal.timber, 1.4)
                inkRect(ctx, -60, -4, 120, 6, pal.woodLight, ink, { line: 1.1 })
                ctx.strokeStyle = pal.woodDark; ctx.lineWidth = 2
                ctx.beginPath(); ctx.moveTo(-48, 0); ctx.quadraticCurveTo(-38, 6, -28, 0); ctx.stroke()
                line(ctx, [[-44, -2], [-44, -24]], pal.woodDark, 2); line(ctx, [[-32, -2], [-32, -14]], pal.woodDark, 2)
                inkRect(ctx, -46, -16, 16, 3, pal.wood, ink, { line: 0.8 })
            }
        }, lampPost(l.x - 90, l.y + 20)],
        decals: [(ctx, pal) => {
            flagstones(ctx, pal, l.x - 10, l.y + 16, 70, 22, 751)
            paintFlowerClump(ctx, pal, l.x + 70, l.y + 12, 752)
        }],
        footprints: [{ x: l.x - 66, y: baseY - 40, w: 160, h: 42 }],
        lights: [{ x: l.x + 33, y: baseY - 34, r: 60 }, { x: l.x - 90, y: l.y - 46, r: 70 }],
        anchors: { cabinChimney: { x: l.x + 65, y: baseY - 130 } }
    }
}

// ---- the phone box ---------------------------------------------------------------------

function phonebox(l) {
    const baseY = l.y
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-26, -104, 26, 6],
            paint(ctx, pal) {
                const ink = pal.ink, red = pal.night ? "#7A2A26" : "#C8342E"
                inkRect(ctx, -20, -88, 40, 88, red, ink, { line: 1.8 })
                inkRect(ctx, -22, -94, 44, 8, red, ink, { line: 1.4 })
                ctx.beginPath(); ctx.moveTo(-22, -94); ctx.quadraticCurveTo(0, -106, 22, -94); ctx.closePath(); ctx.fillStyle = red; ctx.fill(); ctx.stroke()
                inkRect(ctx, -16, -86, 32, 6, "#1E1E1E", ink, { line: 0.8 })
                ctx.fillStyle = "#FFFFFF"; ctx.font = "700 4.5px system-ui"; ctx.textAlign = "center"; ctx.fillText("TELEPHONE", 0, -81.5)
                // glazed door panels
                for (let r = 0; r < 4; r++) for (let c = 0; c < 3; c++) inkRect(ctx, -14 + c * 9.6, -76 + r * 15, 8, 13, pal.night ? pal.windowLit : pal.window, withAlpha(ink, 0.7), { line: 0.8 })
                inkRect(ctx, -20, -4, 40, 4, mix(red, "#000000", 0.3), ink, { line: 1 })
            }
        }],
        decals: [(ctx, pal) => flagstones(ctx, pal, l.x, l.y + 8, 40, 14, 761)],
        footprints: [{ x: l.x - 21, y: baseY - 14, w: 42, h: 15 }],
        lights: [{ x: l.x, y: baseY - 50, r: 50 }]
    }
}

// ---- the coast: the lighthouse -------------------------------------------------------
// A red-and-white tower on a rocky point, a keeper's cottage beside it; the
// lamp's beam sweeps the sea after dark (motion layer).

function lighthouse(l) {
    const baseY = l.y - 6, towerH = 190
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-60, -towerH - 60, 60, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                const red = pal.night ? "#7A2A26" : "#C8453A", white = pal.night ? "#B8BCB6" : "#FAF7EF"
                // tapered tower in bands
                const bands = 6
                for (let i = 0; i < bands; i++) {
                    const y0 = -(i / bands) * towerH, y1 = -((i + 1) / bands) * towerH
                    const w0 = 30 - (i / bands) * 10, w1 = 30 - ((i + 1) / bands) * 10
                    inkPoly(ctx, [[-w0, y0], [w0, y0], [w1, y1], [-w1, y1]], i % 2 ? white : red, ink, { line: 1.5 })
                }
                ctx.save(); ctx.beginPath(); ctx.moveTo(-30, 0); ctx.lineTo(30, 0); ctx.lineTo(20, -towerH); ctx.lineTo(-20, -towerH); ctx.closePath(); ctx.clip()
                ctx.fillStyle = "rgba(0,0,0,0.14)"; ctx.fillRect(6, -towerH, 30, towerH)
                ctx.restore()
                // gallery, lantern room and cap
                inkRect(ctx, -28, -towerH - 6, 56, 7, pal.metal, ink, { line: 1.3 })
                for (let x = -24; x <= 24; x += 6) line(ctx, [[x, -towerH - 6], [x, -towerH - 16]], ink, 0.9)
                line(ctx, [[-26, -towerH - 16], [26, -towerH - 16]], ink, 1.3)
                inkRect(ctx, -15, -towerH - 38, 30, 32, pal.night ? "#FFE7A0" : "#FFF3CC", ink, { line: 1.5 })
                ;[-5, 5].forEach((x) => line(ctx, [[x, -towerH - 38], [x, -towerH - 6]], ink, 1))
                inkPoly(ctx, [[-19, -towerH - 38], [19, -towerH - 38], [0, -towerH - 58]], red, ink, { line: 1.5 })
                inkEllipse(ctx, 0, -towerH - 60, 3, 3, pal.metal, ink, 1)
                // door and little windows up the tower
                ctx.beginPath(); ctx.moveTo(-8, 0); ctx.lineTo(-8, -20); ctx.arc(0, -20, 8, Math.PI, 0); ctx.lineTo(8, 0); ctx.closePath()
                ctx.fillStyle = pal.door; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.3; ctx.stroke()
                ;[-70, -120].forEach((y) => inkRect(ctx, -4, y, 8, 10, pal.night ? pal.windowLit : pal.window, ink, { line: 1 }))
            }
        }, {
            // the keeper's cottage
            x: l.x - 96, y: l.y + 14, box: [-50, -80, 60, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                inkPoly(ctx, [[34, 0], [48, -8], [48, -40], [34, -34]], pal.wallShade, ink)
                inkRect(ctx, -34, -34, 68, 34, pal.wall, ink, { line: 1.6 })
                inkPoly(ctx, [[-40, -32], [0, -66], [40, -32]], pal.roof, ink, { line: 1.6 })
                inkRect(ctx, -8, -24, 14, 24, pal.door, ink, { line: 1.2 })
                inkRect(ctx, 14, -26, 14, 12, pal.night ? pal.windowLit : pal.window, ink, { line: 1 })
            }
        }, ...[[60, 30, 22], [90, 6, 14], [-30, 50, 12], [110, 40, 18]].map(([dx, dy, s]) => ({
            // rocks of the point
            x: l.x + dx, y: l.y + dy, box: [-s * 1.4, -s * 1.2, s * 1.4, 4],
            paint(ctx, pal) {
                ctx.beginPath(); ctx.moveTo(-s, 0); ctx.lineTo(-s * 0.7, -s * 0.8); ctx.lineTo(0, -s * 1.05); ctx.lineTo(s * 0.8, -s * 0.6); ctx.lineTo(s, 0); ctx.closePath()
                ctx.fillStyle = pal.stoneDark; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.4; ctx.stroke()
                ctx.fillStyle = pal.stone; ctx.beginPath(); ctx.moveTo(-s * 0.7, -s * 0.8); ctx.lineTo(0, -s * 1.05); ctx.lineTo(-s * 0.1, -s * 0.4); ctx.closePath(); ctx.fill()
            }
        }))],
        decals: [(ctx, pal) => cobbles(ctx, pal, l.x - 30, l.y + 22, 110, 34, 771)],
        footprints: [{ circle: true, x: l.x, y: baseY - 10, r: 30 }, { x: l.x - 132, y: l.y - 14, w: 84, h: 30 }],
        lights: [{ x: l.x, y: baseY - towerH - 22, r: 110 }, { x: l.x - 75, y: l.y - 6, r: 50 }],
        anchors: { beacon: { x: l.x, y: baseY - towerH - 22 } }
    }
}

// ---- the coast: the beach café --------------------------------------------------------

function cafe(l) {
    const baseY = l.y - 14
    const flags = [["#D52B1E", "#FFFFFF"], ["#0055A4", "#FFFFFF", "#EF4135"], ["#009246", "#FFFFFF", "#CE2B37"], ["#AA151B", "#F1BF00", "#AA151B"], ["#000000", "#DD0000", "#FFCE00"], ["#BC002D", "#FFFFFF"], ["#006AA7", "#FECC00"]]
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-90, -140, 110, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                inkPoly(ctx, [[60, 0], [80, -10], [80, -60], [60, -52]], pal.wallShade, ink)
                inkRect(ctx, -60, -52, 120, 52, pal.night ? "#6E7E86" : "#DCEFF2", ink, { line: 1.8 })
                inkRect(ctx, -48, -40, 44, 26, pal.night ? pal.windowLit : pal.window, ink, { line: 1.4 })
                inkRect(ctx, 10, -44, 26, 44, pal.door, ink, { line: 1.4 })
                inkPoly(ctx, [[-66, -52], [66, -52], [58, -70], [-58, -70]], pal.roof, ink, { line: 1.6 })
                // striped awning
                for (let i = 0; i < 10; i++) {
                    const x = -66 + i * 13.2
                    inkPoly(ctx, [[x, -52], [x + 13.2, -52], [x + 15, -40], [x + 2, -40]], i % 2 ? "#FAF7EF" : "#3E8FB0", ink, { line: 0.9 })
                }
                sign(ctx, pal, 0, -74, "CAFÉ BABEL", "#FFF6D8")
                // a string of flags from everywhere
                ctx.strokeStyle = ink; ctx.lineWidth = 1
                ctx.beginPath(); ctx.moveTo(-86, -118); ctx.quadraticCurveTo(0, -104, 96, -118); ctx.stroke()
                ;[-86, 96].forEach((x) => line(ctx, [[x, 0], [x, -122]], pal.woodDark, 2.6))
                flags.forEach((f, i) => {
                    const t = (i + 0.5) / flags.length, x = -86 + 182 * t, y = -118 + 2 * t * (1 - t) * 14 + 3
                    f.forEach((c, k) => { ctx.fillStyle = c; ctx.fillRect(x - 7 + (k * 14) / f.length, y, 14 / f.length, 10) })
                    ctx.strokeStyle = ink; ctx.lineWidth = 0.8; ctx.strokeRect(x - 7, y, 14, 10)
                })
            }
        }, ...[[-60, 50], [40, 56], [120, 30]].map(([dx, dy], i) => ({
            // tables with parasols
            x: l.x + dx, y: l.y + dy, box: [-30, -70, 30, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                line(ctx, [[0, 0], [0, -52]], pal.metal, 2)
                inkEllipse(ctx, 0, -16, 14, 5, pal.cloth, ink, 1.1)
                line(ctx, [[-10, 0], [-10, -12]], pal.metal, 1.4); line(ctx, [[10, 0], [10, -12]], pal.metal, 1.4)
                ctx.beginPath(); ctx.moveTo(-28, -48); ctx.quadraticCurveTo(0, -72, 28, -48); ctx.closePath()
                ctx.fillStyle = ["#E8543A", "#F2C14E", "#3E8FB0"][i]; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.3; ctx.stroke()
                ;[-14, 0, 14].forEach((x) => line(ctx, [[0, -64], [x, -50]], withAlpha(ink, 0.5), 0.8))
                chairAt(ctx, pal, -20, 4); chairAt(ctx, pal, 20, 4)
            }
        }))],
        decals: [(ctx, pal) => { ctx.save(); ctx.fillStyle = withAlpha(pal.woodLight, 0.95); ctx.fillRect(l.x - 110, l.y + 10, 260, 70); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.strokeRect(l.x - 110, l.y + 10, 260, 70); for (let x = l.x - 110 + 10; x < l.x + 150; x += 10) line(ctx, [[x, l.y + 10], [x, l.y + 80]], pal.woodDark, 0.8, 0.6); ctx.restore() }],
        footprints: [{ x: l.x - 62, y: baseY - 40, w: 144, h: 42 }, ...[[-60, 50], [40, 56], [120, 30]].map(([dx, dy]) => ({ circle: true, x: l.x + dx, y: l.y + dy - 6, r: 14 }))],
        lights: [{ x: l.x - 26, y: baseY - 26, r: 60 }, { x: l.x, y: baseY - 94, r: 100 }]
    }
}

function chairAt(ctx, pal, x, y) {
    inkRect(ctx, x - 5, y - 8, 10, 3, pal.metal, pal.ink, { line: 0.8 })
    line(ctx, [[x - 4, y], [x - 4, y - 6]], pal.metal, 1.2); line(ctx, [[x + 4, y], [x + 4, y - 6]], pal.metal, 1.2)
}

// ---- the coast: the boardwalk and its Ferris wheel ----------------------------------------

function boardwalk(l) {
    const wheel = { x: l.x + 30, y: l.y - 150, r: 92, base: l.y - 20 }
    return {
        sprites: [{
            // the wheel's A-frame (the wheel itself turns in the motion layer)
            x: wheel.x, y: wheel.base, box: [-110, -250, 110, 8],
            paint(ctx, pal) {
                const ink = pal.ink, top = wheel.y - wheel.base
                ;[-1, 1].forEach((side) => {
                    line(ctx, [[side * 70, 0], [0, top]], ink, 6.5); line(ctx, [[side * 70, 0], [0, top]], pal.night ? "#8A8E8A" : "#E8E4DA", 4.2)
                    line(ctx, [[side * 40, 0], [0, top]], ink, 4.4); line(ctx, [[side * 40, 0], [0, top]], pal.night ? "#8A8E8A" : "#E8E4DA", 2.6)
                })
                inkRect(ctx, -80, -6, 160, 8, pal.woodDark, ink, { line: 1.3 })
            }
        }, {
            // the ticket booth
            x: l.x - 120, y: l.y + 10, box: [-34, -86, 34, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                inkRect(ctx, -24, -52, 48, 52, "#E8543A", ink, { line: 1.6 })
                inkRect(ctx, -16, -42, 32, 18, pal.night ? pal.windowLit : pal.window, ink, { line: 1.2 })
                ctx.beginPath(); ctx.moveTo(-30, -52); ctx.lineTo(0, -80); ctx.lineTo(30, -52); ctx.closePath()
                ctx.fillStyle = "#F2C14E"; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.5; ctx.stroke()
                ctx.fillStyle = "#FFFFFF"; ctx.font = `700 6px ${HAND}`; ctx.textAlign = "center"; ctx.fillText("TICKETS", 0, -12)
            }
        }],
        decals: [(ctx, pal) => {
            // the boards: a long deck along the beach
            ctx.save()
            ctx.translate(l.x, l.y + 20); ctx.rotate(-0.2)
            ctx.fillStyle = pal.woodLight; ctx.fillRect(-190, -30, 380, 60)
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.5; ctx.strokeRect(-190, -30, 380, 60)
            for (let x = -184; x < 190; x += 9) line(ctx, [[x, -30], [x, 30]], pal.woodDark, 0.9, 0.7)
            ctx.restore()
        }],
        footprints: [{ x: wheel.x - 82, y: wheel.base - 8, w: 164, h: 10 }, { x: l.x - 146, y: l.y - 16, w: 52, h: 28 }],
        lights: [{ x: wheel.x, y: wheel.y, r: 150 }, { x: l.x - 120, y: l.y - 22, r: 50 }],
        anchors: { ferrisWheel: wheel }
    }
}

// ---- the coast: the fortune teller --------------------------------------------------------

function fortune(l) {
    const baseY = l.y - 6
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-70, -140, 70, 8],
            paint(ctx, pal) {
                const ink = pal.ink, purple = pal.night ? "#4A3A6A" : "#7C5AB8"
                ctx.beginPath(); ctx.moveTo(-60, 0); ctx.lineTo(-50, -70); ctx.quadraticCurveTo(0, -140, 50, -70); ctx.lineTo(60, 0); ctx.closePath()
                ctx.fillStyle = purple; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.8; ctx.stroke()
                ctx.save(); ctx.clip()
                for (let i = 0; i < 7; i++) line(ctx, [[-60 + i * 20, 0], [0, -118]], mix(purple, "#000000", 0.2), 1.4, 0.8)
                ctx.restore()
                // stars and a moon on the canvas
                ;[[-30, -60], [26, -80], [0, -40], [-12, -96], [36, -40]].forEach(([x, y], i) => star(ctx, x, y, i % 2 ? 4 : 6, "#F2C14E", ink))
                ctx.fillStyle = "#F6E7B0"; ctx.beginPath(); ctx.arc(30, -104, 8, 0, Math.PI * 2); ctx.fill()
                ctx.fillStyle = purple; ctx.beginPath(); ctx.arc(34, -107, 7, 0, Math.PI * 2); ctx.fill()
                // the open flap, a table and the crystal ball
                inkPoly(ctx, [[-18, 0], [0, -50], [18, 0]], "#2A1E3A", ink, { line: 1.2 })
                inkRect(ctx, -14, -18, 28, 18, "#8A2A4A", ink, { line: 1.2 })
                inkEllipse(ctx, 0, -26, 8, 8, pal.night ? "#B8D8F0" : "#D8ECF8", ink, 1.3)
                sign(ctx, pal, 0, -128, "FORTUNES", "#F6E7CC")
            }
        }],
        decals: [(ctx, pal) => { for (let i = 0; i < 12; i++) star(ctx, l.x - 60 + (i * 37) % 120, l.y + 20 + (i * 13) % 30, 2.5, withAlpha("#F2C14E", 0.8), null) }],
        footprints: [{ x: l.x - 58, y: baseY - 36, w: 116, h: 38 }],
        lights: [{ x: l.x, y: baseY - 26, r: 70 }],
        anchors: { crystalBall: { x: l.x, y: baseY - 26 } }
    }
}

// ---- the coast: the beach itself ------------------------------------------------------------
// Beach huts, parasols, a sandcastle and rock pools strung along the sand,
// and anchors for the sailboats and the waves.

function beach(coastPoints, byKey, seed) {
    const coast = samplePath(coastPoints, 8)
    const r = rng(seed + 81)
    const sprites = [], footprints = []
    const clear = (x, y) => !Object.values(byKey).some((l) => Math.hypot(l.x - x, l.y - y) < l.r + 50)
    const colors = ["#E8543A", "#3E8FB0", "#F2C14E", "#6FB3A8", "#EE9AA8", "#8E7CC3"]
    let hut = 0
    coast.forEach((p, i) => {
        if (i % 13) return
        const inland = 70 + r() * 40
        const x = p.x - p.nx * inland, y = p.y - p.ny * inland
        if (!clear(x, y)) return
        const color = colors[hut++ % colors.length]
        const kind = r()
        if (kind < 0.45) {
            sprites.push({
                x, y, box: [-24, -60, 24, 6],
                paint(ctx, pal) {
                    const ink = pal.ink
                    inkRect(ctx, -16, -38, 32, 38, color, ink, { line: 1.5 })
                    for (let k = -12; k < 16; k += 8) line(ctx, [[k, -36], [k, -2]], "rgba(255,255,255,0.45)", 3)
                    inkPoly(ctx, [[-20, -36], [0, -54], [20, -36]], "#FAF7EF", ink, { line: 1.4 })
                    inkRect(ctx, -6, -24, 12, 24, mix(color, "#000000", 0.35), ink, { line: 1 })
                }
            })
            footprints.push({ x: x - 17, y: y - 10, w: 34, h: 11 })
        } else if (kind < 0.8) {
            sprites.push({
                x, y, box: [-30, -54, 30, 6],
                paint(ctx, pal) {
                    const ink = pal.ink
                    line(ctx, [[0, 0], [2, -40]], ink, 1.8)
                    ctx.beginPath(); ctx.moveTo(-26, -36); ctx.quadraticCurveTo(2, -58, 30, -36); ctx.closePath()
                    ctx.fillStyle = color; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.3; ctx.stroke()
                    ctx.fillStyle = "#FFFFFF"; ctx.beginPath(); ctx.moveTo(-8, -46); ctx.quadraticCurveTo(2, -54, 12, -46); ctx.lineTo(2, -38); ctx.closePath(); ctx.fill()
                    ctx.save(); ctx.translate(12, -2); ctx.rotate(-0.1)
                    ctx.fillStyle = mix(color, "#FFFFFF", 0.4); ctx.fillRect(-4, -4, 26, 10); ctx.strokeStyle = ink; ctx.lineWidth = 0.9; ctx.strokeRect(-4, -4, 26, 10)
                    ctx.restore()
                }
            })
        } else {
            sprites.push({
                x, y, box: [-20, -30, 20, 6],
                paint(ctx, pal) {
                    // a sandcastle with a flag
                    const sand = pal.sandDark, ink = pal.ink
                    inkRect(ctx, -14, -12, 28, 12, sand, ink, { line: 1.1 })
                    ;[-10, 0, 10].forEach((tx) => inkRect(ctx, tx - 3, -20, 6, 8, sand, ink, { line: 0.9 }))
                    line(ctx, [[0, -20], [0, -30]], ink, 1)
                    inkPoly(ctx, [[0, -30], [8, -27], [0, -24]], "#E8543A", null)
                }
            })
        }
    })
    // sailboats offshore and the lines the waves roll in along
    const offshore = coast.filter((_, i) => i % 40 === 20).map((p, i) => ({ x: p.x + p.nx * (240 + i * 50), y: p.y + p.ny * (240 + i * 50), phase: i * 1.7 }))
    return {
        sprites,
        decals: [(ctx, pal) => {
            // rock pools near the lighthouse point
            const lh = byKey.lighthouse
            if (!lh) return
            for (let i = 0; i < 4; i++) {
                const x = lh.x - 40 + i * 50, y = lh.y + 90 + (i % 2) * 20
                inkEllipse(ctx, x, y, 16, 7, pal.stoneDark, pal.ink, 1.2)
                inkEllipse(ctx, x, y, 11, 4.5, pal.seaShallow, null)
            }
        }],
        footprints,
        lights: [],
        anchors: { sailboats: offshore, coast }
    }
}
